'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { MapPinIcon, Loader2, Search, X } from 'lucide-react';
import { useMapsLibrary } from '@vis.gl/react-google-maps';
import { useDebounceCallback } from 'usehooks-ts';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandList,
} from '@/components/ui/command';
import { Command as CommandPrimitive } from 'cmdk';
import { cn } from '@/lib/utils';

interface AutocompleteAddressInputProps {
  value: string;
  onChange: (value: string) => void;
  onPlaceSelected?: (place: google.maps.places.PlaceResult) => void;
  placeholder?: string;
  className?: string;
  error?: boolean;
  onClear?: () => void;
  /** Restrict autocomplete to specific states (e.g., ['FL', 'TX']). Empty array allows all states. */
  allowedStates?: readonly string[];
}

export function AutocompleteAddressInput({
  value,
  onChange,
  onClear,
  onPlaceSelected,
  placeholder = 'Enter address...',
  className,
  error = false,
  allowedStates,
}: AutocompleteAddressInputProps) {
  const placesLib = useMapsLibrary('places');

  const [predictions, setPredictions] = useState<
    google.maps.places.AutocompletePrediction[]
  >([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const autocompleteServiceRef =
    useRef<google.maps.places.AutocompleteService | null>(null);
  const placesServiceRef = useRef<google.maps.places.PlacesService | null>(
    null
  );

  // Initialize Google Maps services
  useEffect(() => {
    if (placesLib) {
      // Initialize AutocompleteService
      if (!autocompleteServiceRef.current) {
        autocompleteServiceRef.current = new placesLib.AutocompleteService();
      }

      // Initialize PlacesService for getting place details
      if (!placesServiceRef.current) {
        const div = document.createElement('div');
        placesServiceRef.current = new placesLib.PlacesService(div);
      }
    }
  }, [placesLib]);

  // Fetch predictions from Google Places API
  const fetchPredictions = useCallback(
    (query: string) => {
      if (!placesLib || !autocompleteServiceRef.current) return;

      if (!query.trim()) {
        setPredictions([]);
        setIsOpen(false);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      // Build request options with optional state restrictions
      const requestOptions: google.maps.places.AutocompletionRequest = {
        input: query,
        types: ['address'],
      };

      // Add component restrictions if allowed states are specified
      if (allowedStates && allowedStates.length > 0) {
        requestOptions.componentRestrictions = {
          country: 'us',
          // Restrict to specific states by administrative area level 1
          // Note: Google Maps API doesn't directly support state-level filtering
          // We'll filter results client-side after receiving predictions
        };
      }

      autocompleteServiceRef.current.getPlacePredictions(
        requestOptions,
        (predictions, status) => {
          setIsLoading(false);

          if (
            status === google.maps.places.PlacesServiceStatus.OK &&
            predictions
          ) {
            // Filter predictions by state if allowed states are specified
            let filteredPredictions = predictions;
            if (allowedStates && allowedStates.length > 0) {
              filteredPredictions = predictions.filter(prediction => {
                // Extract state from prediction terms
                // The state is typically the second-to-last term in the terms array
                // Format: [street, city, state, country]
                const terms = prediction.terms;
                if (terms.length >= 2) {
                  // State is usually at index -2 (before country which is at index -1)
                  const stateValue = terms[terms.length - 2]?.value;
                  return stateValue && allowedStates.includes(stateValue);
                }
                return false;
              });
            }

            setPredictions(filteredPredictions);
            setIsOpen(true);
          } else {
            setPredictions([]);
            setIsOpen(false);
          }
        }
      );
    },
    [placesLib, allowedStates]
  );

  // Use usehooks-ts debounce hook
  const debouncedFetchPredictions = useDebounceCallback(fetchPredictions, 500);

  // Handle input change
  const handleInputChange = (newValue: string) => {
    onChange(newValue);

    if (newValue.trim()) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
      setPredictions([]);
    }

    debouncedFetchPredictions(newValue);
  };

  // Handle clear/reset
  const handleClear = () => {
    onChange('');
    setPredictions([]);
    setIsOpen(false);
    onClear?.();
  };

  // Handle address selection
  const handleSelectAddress = (
    prediction: google.maps.places.AutocompletePrediction
  ) => {
    onChange(prediction.description);
    setPredictions([]);
    setIsOpen(false);

    // Get detailed place information
    if (placesServiceRef.current && onPlaceSelected) {
      placesServiceRef.current.getDetails(
        {
          placeId: prediction.place_id,
          fields: [
            'formatted_address',
            'address_components',
            'geometry',
            'name',
          ],
        },
        (place, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && place) {
            onPlaceSelected(place);
          }
        }
      );
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const open = useCallback(() => {
    if (value.trim()) setIsOpen(true);
  }, [value]);

  const close = useCallback(() => setIsOpen(false), []);

  if (!placesLib) {
    return (
      <div
        className={cn('rounded-lg border shadow-md p-3 text-center', className)}
      >
        <p className="text-sm text-muted-foreground">Loading Maps...</p>
      </div>
    );
  }

  return (
    <Command
      shouldFilter={false}
      onKeyDown={handleKeyDown}
      className={cn('overflow-visible', className)}
    >
      <div
        className={cn(
          'relative flex w-full items-center justify-between rounded-lg border-2 border-neutral-200 dark:border-0 dark:bg-neutral-800 ring-offset-background text-sm focus-within:ring-2 focus-within:ring-offset-2',
          error
            ? 'border-destructive focus-within:ring-destructive'
            : 'focus-within:ring-ring'
        )}
      >
        <Search
          className={cn(
            'absolute left-3 h-4 w-4 pointer-events-none',
            error ? 'text-destructive' : 'text-muted-foreground'
          )}
        />
        <CommandPrimitive.Input
          value={value}
          onValueChange={handleInputChange}
          onBlur={close}
          onFocus={open}
          placeholder={placeholder}
          className="w-full p-3 pl-10 pr-10 rounded-lg outline-none border-0"
        />
        {value && (
          <button
            type="button"
            onClick={handleClear}
            className={cn(
              'absolute right-3 h-4 w-4 transition-colors',
              error
                ? 'text-destructive hover:text-destructive/80'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {isOpen && (
        <div className="relative animate-in fade-in-0 zoom-in-95 h-auto">
          <CommandList>
            <div className="absolute top-1.5 z-50 w-full">
              <CommandGroup className="relative h-auto z-50 min-w-[8rem] overflow-hidden rounded-md border shadow-md bg-background">
                {isLoading ? (
                  <div className="h-28 flex items-center justify-center">
                    <Loader2 className="size-6 animate-spin" />
                  </div>
                ) : (
                  <>
                    {predictions.map(prediction => (
                      <CommandPrimitive.Item
                        value={prediction.description}
                        onSelect={() => handleSelectAddress(prediction)}
                        className="flex select-text flex-col cursor-pointer gap-0.5 h-max p-2 px-3 rounded-md aria-selected:bg-accent aria-selected:text-accent-foreground hover:bg-accent hover:text-accent-foreground items-start"
                        key={prediction.place_id}
                        onMouseDown={e => e.preventDefault()}
                      >
                        <div className="flex items-start gap-2 w-full">
                          <MapPinIcon className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                          <div className="flex flex-col flex-1">
                            <span className="text-sm font-medium">
                              {prediction.structured_formatting.main_text}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {prediction.structured_formatting.secondary_text}
                            </span>
                          </div>
                        </div>
                      </CommandPrimitive.Item>
                    ))}
                  </>
                )}

                <CommandEmpty>
                  {!isLoading && predictions.length === 0 && (
                    <div className="py-4 flex items-center justify-center text-sm text-muted-foreground">
                      {value === ''
                        ? 'Please enter an address'
                        : 'No address found'}
                    </div>
                  )}
                </CommandEmpty>
              </CommandGroup>
            </div>
          </CommandList>
        </div>
      )}
    </Command>
  );
}
